import { FC } from "react";
import { useDocumentPage } from "./DocumentPage.hooks";
import { GeneralTemplate } from "@/components/templates/GeneralTemplate";
import { MenuButtonView } from "@/components/MenuButtonView";
import { AlertMessageDialog } from "@/components/MessageDialog";
import { ChatBubbleIcon, Cross2Icon, DownloadIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { MobileView, PcView } from "@/components/DisplayBreakpointView";
import { AppVersion } from "@/components/AppVersion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import IconBookmark from "@/assets/images/icon_bookmark.svg";
import IconBookmarkFilled from "@/assets/images/icon_bookmark_filled.svg";
import IconThumbsUp from "@/assets/images/icon_thumbs_up.svg";
import IconFileSysLink from "@/assets/images/file-symlink.svg";
import { formatToKilo } from "@/utils/numberFormat";
import { Toggle } from "@/components/ui/toggle";
import { DocumentChat } from "./DocumentChat/DocumentChat";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SystemHeader } from "@/components/SystemHeader/SystemHeader";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/utils/tailwind/utils";
import { ExpandableList } from "@/pages/Document/ExpandableList";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DocumentVersionTag } from "@/components/DocumentVersionTag";
import { SessionErrorPage } from "@/pages/SessionError/SessionError";

export const DocumentPage: FC = () => {
  const {
    t,
    loading,
    documentData,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    iframeSrcUrl,
    bookmarkOn,
    onClickBookmark,
    totalDocumentGood,
    isGoodDocument,
    onClickGood,
    openDocumentChat,
    onClickOpenDocumentChat,
    onClickCloseDocumentChat,
    documentChatHooks,
    openDocumentDetail,
    onClickOpenDocumentDetail,
    onClickCloseDocumentDetail,
    canPreview,
    page,
    totalCategories,
    totalLanguages,
    timestamp,
    firLink,
    relatedDocuments,
    forbiddenError,
  } = useDocumentPage();

  const deviceErrorView = (
    <Alert>
      <InfoCircledIcon className="h-4 w-4" />
      <AlertTitle>{t("document_device_error")}</AlertTitle>
      <AlertDescription>{t("document_device_error_message")}</AlertDescription>
    </Alert>
  );

  const fileTypeErrorView = (
    <Alert>
      <InfoCircledIcon className="h-4 w-4" />
      <AlertTitle>{t("document_file_error")}</AlertTitle>
      <AlertDescription>{t("document_file_error_message")}</AlertDescription>
    </Alert>
  );

  const detailView = (
    <div className={"flex flex-col"}>
      {relatedDocuments != null && (
        <>
          <div className={"border-t-4 border-foreground opacity-50"} />
          <div>
            <h3 className={"mt-3 text-sm font-bold"}>{t("document_related_documents")}</h3>
            {Object.entries(relatedDocuments).map(([categoryId, documents]) => {
              const relatedCategory = totalCategories.find((c) => c.code === categoryId);

              return (
                <Accordion type="single" collapsible>
                  <AccordionItem value="item">
                    <AccordionTrigger className={"break-words py-2 text-xs"}>
                      {relatedCategory ? relatedCategory.name : t("document_related_documents")}
                    </AccordionTrigger>
                    <AccordionContent>
                      {documents.map((document) => (
                        <ul className={"list-disc"}>
                          <li className={"flex flex-row"}>
                            <span className={"text-sm"}>・</span>
                            <Link
                              key={`${document.id}_${categoryId}`}
                              to={`/documents/${document.id}`}
                              target={"_blank"}
                              className={"flex-1 break-words border-0 text-sm text-blue-600"}
                            >
                              {document.title}
                            </Link>
                          </li>
                        </ul>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              );
            })}
          </div>
        </>
      )}
      {documentData?.metadata && (
        <div className="flex h-[200px] flex-col">
          <div className="rounded-md bg-secondary">
            <div className="grid grid-cols-1 p-2">
              <h3 className={"mb-2 text-sm font-bold"}>{t("document_info")}</h3>
              <ul>
                <li className={"text-xs"}>{documentData.fileName}</li>
                {documentData.metadata.createdAt && (
                  <li className={"text-xs"}>
                    {new Date(documentData.metadata.createdAt).toLocaleString()} {t("create")}
                  </li>
                )}
                {documentData.metadata.updatedAt && (
                  <li className={"text-xs"}>
                    {new Date(documentData.metadata.updatedAt).toLocaleString()} {t("update")}
                  </li>
                )}
                {documentData.metadata.modelGroup && (
                  <>
                    <li>
                      <label className={"text-xs font-bold"}>{t("filter_model_gr")}</label>
                    </li>
                    <li className={"text-xs"}>{documentData.metadata.modelGroup.join(", ")}</li>
                  </>
                )}
                {documentData.metadata.categories && (
                  <>
                    <li>
                      <label className={"text-xs font-bold"}>{t("filter_info_category")}</label>
                    </li>
                    <li className={"text-xs"}>
                      {documentData.metadata.categories
                        .map((category) => {
                          const relatedCategory = totalCategories.find((v) => v.code === category);
                          return relatedCategory ? relatedCategory.name : t("filter_info_category_none");
                        })
                        .join(", ")}
                    </li>
                  </>
                )}
                {documentData.metadata.langId && (
                  <>
                    <li>
                      <label className={"text-xs font-bold"}>{t("filter_language")}</label>
                    </li>
                    <li className={"text-xs"}>
                      {totalLanguages.find((v) => v.code === documentData.metadata?.langId)?.name}
                    </li>
                  </>
                )}
                {documentData.metadata.modelCode && (
                  <ExpandableList title={t("filter_model_code")} items={documentData.metadata.modelCode} />
                )}
                {documentData.metadata.modelName && (
                  <ExpandableList title={t("filter_model_name")} items={documentData.metadata.modelName} />
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const downloadButton = (
    <Button variant="default" className="min-h-10 w-full" type="button" disabled={documentData == null}>
      <a
        href={documentData?.sourceUrl}
        download={documentData?.fileName}
        target="_blank"
        className="flex flex-1 flex-row items-center justify-center gap-x-2"
      >
        <DownloadIcon className="h-4 w-4" />
        {t("download")}
      </a>
    </Button>
  );

  const externalLinkButton = (
    <Button variant="outline" className="mt-2 min-h-10 w-full" type="button" disabled={documentData == null}>
      <a
        href={documentData?.externalLink}
        target="_blank"
        className="flex flex-1 flex-row items-center justify-center gap-x-2"
      >
        <img src={IconFileSysLink} className="h-4 w-4" />
        {t("external_link")}
      </a>
    </Button>
  );

  const titleAndBookmark = (
    <>
      <p className="max-h-8 overflow-hidden text-ellipsis whitespace-nowrap text-base font-normal">
        {documentData?.title ?? ""}
      </p>
      {documentData?.isLatestVersion === false && (
        <DocumentVersionTag className={"ml-2"} isLatestVersion={documentData?.isLatestVersion} />
      )}
      <Button variant="ghost" className="ml-2 h-10 min-w-10 p-0" onClick={onClickBookmark}>
        {bookmarkOn && <img src={IconBookmarkFilled} className="h-4 w-4" />}
        {!bookmarkOn && <img src={IconBookmark} className="h-4 w-4" />}
      </Button>
    </>
  );

  const goodToggle = (
    <Toggle
      variant={"default"}
      data-state={isGoodDocument && "on"}
      className="h-8 min-w-16 gap-1"
      onClick={onClickGood}
    >
      <img src={IconThumbsUp} className="h-4 w-4" />
      <div className="flex w-full justify-end">
        <p>{formatToKilo(totalDocumentGood)}</p>
      </div>
    </Toggle>
  );

  if (forbiddenError) {
    return <SessionErrorPage errorType={"forbidden"} />;
  }

  return (
    <>
      <PcView>
        <GeneralTemplate
          header={
            <>
              <SystemHeader />
              <div className="flex w-full flex-col items-center border-b-[1px] border-gray-200 bg-white">
                <div className="flex h-14 w-full flex-row items-center px-2">
                  <Button className="h-10 w-10 p-0" variant="ghost" type="button" onClick={() => window.close()}>
                    <Cross2Icon className="h-4 w-4" />
                  </Button>
                  {documentData && (
                    <div className="flex max-w-[calc(100svw-156px)] flex-row items-center">
                      {titleAndBookmark}
                      {goodToggle}
                    </div>
                  )}
                  <div className="flex-1" />
                  <MenuButtonView firLink={firLink} />
                </div>
              </div>
            </>
          }
          loading={loading}
          dialog={
            <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
          }
        >
          <div className="flex h-[calc(100svh-108px)] w-full flex-row">
            {/* left sidebar */}
            {openDocumentChat && (
              <div
                className={cn(
                  "relative flex flex-col border-r bg-white px-4 pt-4",
                  canPreview ? "w-[340px]" : "flex-grow",
                )}
              >
                <DocumentChat
                  {...documentChatHooks}
                  ref={documentChatHooks.scrollAnchorRef}
                  messageLinkType={canPreview ? "self" : "blank"}
                />
                <button
                  className="absolute right-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none"
                  onClick={onClickCloseDocumentChat}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {/* main */}
            <div className="flex flex-grow">
              {documentData != null && (
                <>
                  {!canPreview && !openDocumentChat && (
                    <div className="mx-auto flex items-center p-4">{deviceErrorView}</div>
                  )}
                  {canPreview && (
                    <>
                      {documentData.contentType === "PDF" && (
                        // NOTE: Keyを付与してpageが変更されるとiframeを再renderする
                        <iframe key={`${page}-${timestamp}`} className="flex-1 p-4" src={iframeSrcUrl} />
                      )}
                      {documentData.contentType !== "PDF" && (
                        <div className="mx-auto flex items-center p-4">{fileTypeErrorView}</div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {/* right sidebar */}
            {(!canPreview || openDocumentDetail) && (
              <div
                className={cn(
                  "relative flex w-[288px] flex-col bg-white px-4 pb-4",
                  canPreview ? "w-[288px]" : "min-w-[288px]",
                )}
              >
                <ScrollArea className="h-[calc(100svh-57px)] w-full">
                  <div className={"py-4"}>
                    {detailView}
                    <div className={"h-20"} />
                  </div>
                </ScrollArea>
                <div className={"mb-7"}>
                  {downloadButton}
                  {documentData?.externalLink && externalLinkButton}
                </div>
                {canPreview && (
                  <div className="absolute left-[-25px] top-2">
                    <Button variant="outline" className="h-9 w-9 p-0" onClick={onClickCloseDocumentDetail}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            {canPreview && !openDocumentDetail && (
              <div className="flex flex-col justify-between bg-white p-2">
                <Button variant="outline" className="h-9 w-9 p-0" onClick={onClickOpenDocumentDetail}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="default" className="h-9 w-9 p-0" type="button" disabled={documentData == null}>
                  <a
                    href={documentData?.sourceUrl}
                    download={documentData?.fileName}
                    target="_blank"
                    className="flex flex-1 flex-row justify-center gap-x-2"
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            )}
            <div className="absolute bottom-8 left-8">
              {!openDocumentChat && (
                <Button
                  className="h-12 w-12 rounded-full border border-white bg-black"
                  onClick={onClickOpenDocumentChat}
                >
                  <ChatBubbleIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {(!canPreview || openDocumentDetail) && <AppVersion />}
        </GeneralTemplate>
      </PcView>

      <MobileView>
        <GeneralTemplate
          header={
            <>
              <SystemHeader />
              <div className="flex h-[104px] w-full flex-col gap-y-2 border-b-[1px] border-gray-200 bg-white py-2">
                <div className="flex h-10 w-full flex-row items-center pl-4 pr-2">
                  <Button className="h-10 w-10 p-0" variant="ghost" type="button" onClick={() => window.close()}>
                    <Cross2Icon className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 px-4"></div>
                  <MenuButtonView firLink={firLink} />
                </div>
                <div className="flex h-10 w-full flex-row items-center pl-4 pr-2">
                  {documentData && (
                    <>
                      {titleAndBookmark}
                      {goodToggle}
                    </>
                  )}
                </div>
              </div>
            </>
          }
          loading={loading}
          dialog={
            <>
              <Dialog open={openDocumentChat} onOpenChange={onClickCloseDocumentChat}>
                <DialogContent
                  className="flex h-full max-w-full flex-col px-4 pb-0 pt-4"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <DocumentChat {...documentChatHooks} ref={documentChatHooks.scrollAnchorRef} />
                </DialogContent>
              </Dialog>
              <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
            </>
          }
        >
          <ScrollArea className="h-[calc(100svh-105px)] w-full bg-white">
            <div className="flex flex-col p-4">
              <div className="mx-auto flex items-center">{deviceErrorView}</div>
              <div className="mb-4 py-4">{detailView}</div>
              <div className={"h-8"}></div>
            </div>
          </ScrollArea>

          <div className="w-full bg-white px-4 pb-4">
            <div className={"mb-7"}>
              {downloadButton}
              {documentData?.externalLink && externalLinkButton}
            </div>
            <div className="absolute bottom-4 left-4">
              {!openDocumentChat && (
                <Button
                  className="h-12 w-12 rounded-full border border-white bg-black"
                  onClick={onClickOpenDocumentChat}
                >
                  <ChatBubbleIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <AppVersion />
        </GeneralTemplate>
      </MobileView>
    </>
  );
};
